import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { useTranslation } from "react-i18next";
import { RiArrowDownSLine } from "@remixicon/react";
import { cn } from "~/lib/utils";
import { CategoryIcon } from "./feature-category-icons";
import type { ProductAllFeaturesCategory } from "~/data/products/types";

interface FeatureAccordionsProps {
  categories: ProductAllFeaturesCategory[];
  headingLabel: string;
  expandAllLabel: string;
  collapseAllLabel: string;
  openItems: string[];
  onOpenItemsChange: (slugs: string[]) => void;
}

export function FeatureAccordions({
  categories,
  headingLabel,
  expandAllLabel,
  collapseAllLabel,
  openItems,
  onOpenItemsChange,
}: FeatureAccordionsProps) {
  const { t } = useTranslation("products");
  const allSlugs = categories.map((c) => c.slug);
  const allExpanded = openItems.length === allSlugs.length;

  return (
    <section className="fleet:bg-transparent bg-white pb-12 md:pb-24">
      <div className="wrap flex flex-col gap-4">
        <div className="hidden items-center justify-between md:flex">
          <h2 className="font-display text-text-heading text-display leading-[1.15] font-extrabold">
            {t(headingLabel)}
          </h2>
          <button
            data-sid="all-features-expand-all"
            type="button"
            className="text-bouncie-600 fleet:text-text-body-tertiary text-base font-bold tracking-[0.16px] underline"
            onClick={() => onOpenItemsChange(allExpanded ? [] : allSlugs)}
          >
            {t(allExpanded ? collapseAllLabel : expandAllLabel)}
          </button>
        </div>

        <AccordionPrimitive.Root
          type="multiple"
          value={openItems}
          onValueChange={onOpenItemsChange}
          className="flex flex-col gap-4"
        >
          {categories.map(({ slug, icon, category }) => (
            <AccordionPrimitive.Item
              key={slug}
              value={slug}
              id={slug}
              className="fleet:border-(--fleet-border-subtle) scroll-mt-20 overflow-hidden rounded-2xl border border-neutral-100"
            >
              <AccordionPrimitive.Header data-sid={`all-features-accordion-header:${slug}`}>
                <AccordionPrimitive.Trigger
                  data-sid={`all-features-accordion-trigger:${slug}`}
                  className="group flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-4 md:px-6"
                >
                  <span className="flex flex-1 items-center gap-2 text-left">
                    <span className="fleet:border-bg-alt fleet:bg-bg-alt flex size-8 shrink-0 items-center justify-center rounded-2xl border border-neutral-100">
                      <CategoryIcon icon={icon} className="text-text-heading fleet:text-icon-primary size-5" />
                    </span>
                    <span className="font-display text-text-heading text-md leading-[1.1] font-extrabold tracking-[0.18px]">
                      {t(category.label)}
                    </span>
                  </span>
                  <RiArrowDownSLine className="text-text-heading fleet:text-icon-primary size-6 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
                <div className="fleet:border-(--fleet-border-subtle) fleet:bg-transparent grid grid-cols-1 border-t-[0.5px] border-neutral-100/50 bg-neutral-50/85 md:grid-cols-3">
                  {category.cards.map((card, index) => (
                    <div
                      key={card.title}
                      id={card.title}
                      className={cn(
                        "scroll-mt-20 flex flex-col gap-1.5 border-neutral-100/50 fleet:border-(--fleet-border-subtle) px-4 py-6 md:px-7",
                        index !== 0 && "border-t-[0.5px] md:border-t-0",
                        index % 3 !== 0 && "md:border-l-[0.5px]",
                        index >= 3 && "md:border-t-[0.5px]",
                      )}
                    >
                      <p className="text-text-heading text-base leading-5 font-bold tracking-[0.16px]">{t(card.title)}</p>
                      <p className="text-text-body-alt text-sm leading-5">{t(card.description)}</p>
                    </div>
                  ))}
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          ))}
        </AccordionPrimitive.Root>
      </div>
    </section>
  );
}
